import { useState } from "react";
import { Calendar, Loader2, Plus, Settings, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useWorkspacePermissions } from "@/hooks/use-workspace-permissions";
import { isNotFound } from "@/lib/api-client";
import { type CalIntegration, useCalIntegration, useDeleteCalIntegration } from "@/services/cal-integration.query";
import { calRefusalOf } from "@/utils/cal-integration-rules";
import { CalIntegrationDialog } from "./CalIntegrationDialog";

interface CalIntegrationCardProps {
  agentId: string;
}

/**
 * The agent's Cal.com connection. The key is write-only: the card shows its
 * hint. Connecting and changing it are for owners, admins and developers, and
 * disconnecting for owners and admins — the function refuses anyone else, so
 * nobody else is offered the buttons.
 */
export function CalIntegrationCard({ agentId }: CalIntegrationCardProps) {
  const { data: connection, isLoading, error, refetch } = useCalIntegration(agentId);
  const { canEdit, canDelete } = useWorkspacePermissions();
  const disconnect = useDeleteCalIntegration();
  const disconnecting = disconnect.isPending;

  /** What the dialog was opened on: null to connect, the connection to change it. Unset while closed. */
  const [dialogFor, setDialogFor] = useState<CalIntegration | null>();
  /** The connection the open confirmation disconnects. */
  const [confirming, setConfirming] = useState<CalIntegration>();
  const [disconnectError, setDisconnectError] = useState<string>();

  const confirmDisconnect = (row: CalIntegration) => {
    setDisconnectError(undefined);
    setConfirming(row);
  };

  const handleDisconnect = async () => {
    setDisconnectError(undefined);
    try {
      await disconnect.mutateAsync(confirming!);
      setConfirming(undefined);
    } catch (failure) {
      setDisconnectError(calRefusalOf(failure, "Cal.com could not be disconnected.").message);
    }
  };

  const body = () => {
    if (isLoading) {
      return (
        <div role="status" aria-label="Loading Cal.com connection">
          <Skeleton className="h-16 w-full" />
        </div>
      );
    }
    // A 404 is final — the agent was deleted, or the user cannot see it — so
    // nothing offers to send its id again. It replaces even an answer the card
    // had: a re-read after a refused change can find the agent gone.
    const gone = isNotFound(error);
    // Otherwise no answer, and none on its way: the read failed. (A re-read
    // that fails for another reason keeps the last answer on screen.)
    if (gone || connection === undefined) {
      return (
        <div role="alert" className="py-6 text-center" data-testid="cal-load-error">
          <p className="mb-4 text-sm text-muted-foreground">
            {gone
              ? "This agent was deleted, or you don't have access to it."
              : "The Cal.com connection could not be loaded."}
          </p>
          {!gone && (
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          )}
        </div>
      );
    }
    if (connection === null) {
      return (
        <div className="py-6 text-center">
          <Calendar className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <p className="mb-4 text-sm text-muted-foreground" data-testid="cal-not-connected">
            Cal.com is not connected.
          </p>
          {canEdit && (
            <Button onClick={() => setDialogFor(null)} data-testid="cal-connect">
              <Plus className="mr-2 h-4 w-4" />
              Connect Cal.com
            </Button>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Badge variant="default" className="mb-2" data-testid="cal-connected">
              Connected
            </Badge>
            <p className="text-sm text-muted-foreground">
              Event Type ID: <span data-testid="cal-event-type-id">{connection.cal_event_type_id}</span>
            </p>
            <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
              API key: <span data-testid="cal-key-hint">{connection.cal_api_key_hint}</span>
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                aria-label="Edit Cal.com connection"
                onClick={() => setDialogFor(connection)}
                data-testid="cal-edit"
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                aria-label="Disconnect Cal.com"
                onClick={() => confirmDisconnect(connection)}
                data-testid="cal-disconnect"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-lg border bg-muted/50 p-4">
          <p className="mb-2 text-sm font-medium">Available Tools:</p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• check_availability_and_requirements</li>
            <li>• book_appointment</li>
          </ul>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card data-testid="cal-integration">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Cal.com Integration
          </CardTitle>
          <CardDescription>Enable dynamic appointment booking powered by Cal.com</CardDescription>
        </CardHeader>
        <CardContent>{body()}</CardContent>
      </Card>

      {dialogFor !== undefined && (
        <CalIntegrationDialog agentId={agentId} connection={dialogFor} onClose={() => setDialogFor(undefined)} />
      )}

      {/* A disconnect in flight finishes in view; a refusal stays on screen with it. */}
      <AlertDialog open={!!confirming} onOpenChange={() => !disconnecting && setConfirming(undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Cal.com?</AlertDialogTitle>
            <AlertDialogDescription>
              The agent will no longer be able to check availability or book appointments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {disconnectError && (
            <p role="alert" data-testid="cal-disconnect-error" className="text-sm text-destructive">
              {disconnectError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting} data-testid="cal-disconnect-cancel">
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={disconnecting}
              aria-busy={disconnecting}
              data-testid="cal-disconnect-confirm"
            >
              {disconnecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
